/**
 * Shop by Pattern Carousel
 *
 * Features:
 * - Infinite loop with cloned items
 * - Smooth transitions with center-focused scaling
 * - Touch/swipe support for mobile
 * - Keyboard navigation
 * - Automatic centering of the current item
 * - Uniform tiles, each labelled with its pattern name
 */

if (!customElements.get('shop-by-pattern-carousel')) {
  /* Auto-scroll tuning (added 2026-07-28).
     22 px/sec is a deliberate crawl: at the 216px desktop tile plus the 32px
     gap that is one tile every ~11 seconds, slow enough to read as ambient
     motion rather than as the carousel advancing on its own. */
  const AUTO_SCROLL_SPEED = 22;

  /* Longest frame we will integrate, in seconds. A GC pause or a throttled
     background tab can hand rAF a huge delta; clamping keeps that from
     teleporting the track. */
  const AUTO_SCROLL_MAX_FRAME = 0.05;

  /* Slightly longer than the 0.4s track transition, so a resume never lands
     mid-snap and cancel an animation by re-applying `transition: none`. */
  const AUTO_SCROLL_RESUME_DELAY = 450;

  /* Pointer travel, in px, at which a press stops being a click and becomes a
     drag. Below this the gesture is left completely alone: no preventDefault,
     no pointer capture, no click suppressor, nothing touched on the track - so
     a tile's <a> activates exactly as it would with no script at all. */
  const DRAG_THRESHOLD = 5;

  class ShopByPatternCarousel extends HTMLElement {
    constructor() {
      super();

      // State
      this.currentIndex = 0;
      this.totalItems = 0;
      this.originalItems = [];
      this.isDragging = false;
      this.startX = 0;
      this.startY = 0;
      this.hasMoved = false;
      this.swipeDirection = null;
      this.currentTranslate = 0;
      this.prevTranslate = 0;
      this.animationID = null;
      this.isTransitioning = false;

      // Pointer-drag state (mouse/pen click-and-drag on the track container).
      // Kept separate from the touch/mouse state above so the two paths never
      // share bookkeeping; see setupPointerDrag() for how they are kept apart.
      this.dragContainer = null;
      this.pointerDragBound = false;
      this.pointerDragActive = false;
      // "Active" means a press is being watched; "committed" means it has
      // passed DRAG_THRESHOLD and is now definitely a drag. Only a committed
      // drag is allowed to interfere with the click that follows.
      this.pointerDragCommitted = false;
      this.pointerGestureBound = false;
      this.activePointerId = null;
      this.pointerStartX = 0;
      this.pointerStartTranslate = 0;
      this.pointerMovedBy = 0;
      this.suppressClickHandler = null;
      this.suppressClickTimer = null;

      // Auto-scroll state (slow continuous drift).
      // autoScrollStopped is the permanent "user took control" latch; every
      // other flag below is a temporary pause reason evaluated together in
      // updateAutoScrollState().
      this.autoScrollEnabled = false;
      this.autoScrollStopped = false;
      this.autoScrollRafId = null;
      this.autoScrollLast = 0;
      this.autoScrollAccum = 0;
      this.autoScrollStep = 0;
      this.autoScrollOwnsTransition = false;
      this.autoScrollHovered = false;
      this.autoScrollFocused = false;
      this.autoScrollDragging = false;
      this.autoScrollVisible = true;
      this.autoScrollObserver = null;
      this.autoScrollResumeTimer = null;
      this.reducedMotionQuery = null;

      // Elements
      this.track = null;
      this.prevButton = null;
      this.nextButton = null;

      // Bound methods for event listeners
      this.autoScrollTick = this.autoScrollTick.bind(this);
      this.handleAutoScrollPointerEnter = this.handleAutoScrollPointerEnter.bind(this);
      this.handleAutoScrollPointerLeave = this.handleAutoScrollPointerLeave.bind(this);
      this.handleAutoScrollFocusIn = this.handleAutoScrollFocusIn.bind(this);
      this.handleAutoScrollFocusOut = this.handleAutoScrollFocusOut.bind(this);
      this.handleAutoScrollVisibility = this.handleAutoScrollVisibility.bind(this);
      this.handleAutoScrollTouchStart = this.handleAutoScrollTouchStart.bind(this);
      this.handleAutoScrollTouchEnd = this.handleAutoScrollTouchEnd.bind(this);
      this.handleUserTakeover = this.handleUserTakeover.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.handlePointerUp = this.handlePointerUp.bind(this);
      this.handleDragStart = this.handleDragStart.bind(this);
      this.handlePrevClick = this.handlePrevClick.bind(this);
      this.handleNextClick = this.handleNextClick.bind(this);
      this.handleTouchStart = this.handleTouchStart.bind(this);
      this.handleTouchMove = this.handleTouchMove.bind(this);
      this.handleTouchEnd = this.handleTouchEnd.bind(this);
      this.handleKeyDown = this.handleKeyDown.bind(this);
      this.handleResize = this.debounce(this.handleResize.bind(this), 250);
      this.handleTransitionEnd = this.handleTransitionEnd.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('.hanlyn-shop-pattern__track');
      this.originalItems = Array.from(this.querySelectorAll('.hanlyn-shop-pattern__item'));
      this.prevButton = this.querySelector('.hanlyn-shop-pattern__arrow--prev');
      this.nextButton = this.querySelector('.hanlyn-shop-pattern__arrow--next');

      this.totalItems = this.originalItems.length;

      if (!this.track || this.totalItems === 0) return;

      // Clone items for infinite loop effect
      this.cloneItems();

      // Set up event listeners
      if (this.prevButton) {
        this.prevButton.addEventListener('click', this.handlePrevClick);
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', this.handleNextClick);
      }

      // Touch events for swipe support
      // Note: touchstart must be passive: false to allow preventDefault
      this.track.addEventListener('touchstart', this.handleTouchStart, { passive: false });
      this.track.addEventListener('touchmove', this.handleTouchMove, { passive: false });
      this.track.addEventListener('touchend', this.handleTouchEnd);

      // Mouse events for desktop drag
      this.track.addEventListener('mousedown', this.handleTouchStart);
      this.track.addEventListener('mousemove', this.handleTouchMove);
      this.track.addEventListener('mouseup', this.handleTouchEnd);
      this.track.addEventListener('mouseleave', this.handleTouchEnd);

      // Transition end for seamless loop
      this.track.addEventListener('transitionend', this.handleTransitionEnd);

      // Keyboard navigation
      this.addEventListener('keydown', this.handleKeyDown);

      // Window resize
      window.addEventListener('resize', this.handleResize);

      // Prevent link navigation after drag
      this.track.addEventListener('click', (e) => {
        const link = e.target.closest('.hanlyn-shop-pattern__card');
        if (link && this.hasMoved) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);

      // Click-and-drag with a mouse/pen. Additive: the arrow clicks, keyboard
      // navigation and touch swipe above are all left exactly as they were.
      this.setupPointerDrag();

      // Initial setup - start at the requested item (after clones), else the first real item.
      // data-start-index lets an embedding section open the carousel on a specific tile
      // (e.g. the active category on a collection page).
      let startOffset = 0;
      const requestedStart = parseInt(this.dataset.startIndex, 10);
      if (!Number.isNaN(requestedStart) && requestedStart > 0 && requestedStart < this.totalItems) {
        startOffset = requestedStart;
      }
      this.currentIndex = this.totalItems + startOffset;
      this.updateCarousel(false);

      // Slow ambient drift. Started last so currentTranslate is already the
      // resting offset for currentIndex before the first frame runs.
      this.setupAutoScroll();
    }

    cloneItems() {
      // Clone all items and prepend/append for infinite loop
      const clonesAfter = [];

      // Create clones for appending (forward scrolling)
      this.originalItems.forEach(item => {
        const cloneAfter = item.cloneNode(true);
        this.markClone(cloneAfter);
        clonesAfter.push(cloneAfter);
      });

      // Create clones for prepending (backward scrolling) - in reverse order
      // So that last item appears immediately before first real item
      for (let i = this.originalItems.length - 1; i >= 0; i--) {
        const cloneBefore = this.originalItems[i].cloneNode(true);
        this.markClone(cloneBefore);
        this.track.insertBefore(cloneBefore, this.track.firstChild);
      }

      // Append clones (for scrolling forwards)
      clonesAfter.forEach(clone => {
        this.track.appendChild(clone);
      });
    }

    // Hide duplicated clones from assistive tech and keyboard navigation
    markClone(clone) {
      clone.classList.add('hanlyn-shop-pattern__item--clone');
      clone.setAttribute('aria-hidden', 'true');
      const card = clone.querySelector('.hanlyn-shop-pattern__card');
      if (card) {
        card.setAttribute('tabindex', '-1');
      }
    }

    disconnectedCallback() {
      if (this.prevButton) {
        this.prevButton.removeEventListener('click', this.handlePrevClick);
      }

      if (this.nextButton) {
        this.nextButton.removeEventListener('click', this.handleNextClick);
      }

      if (this.track) {
        this.track.removeEventListener('touchstart', this.handleTouchStart);
        this.track.removeEventListener('touchmove', this.handleTouchMove);
        this.track.removeEventListener('touchend', this.handleTouchEnd);
        this.track.removeEventListener('mousedown', this.handleTouchStart);
        this.track.removeEventListener('mousemove', this.handleTouchMove);
        this.track.removeEventListener('mouseup', this.handleTouchEnd);
        this.track.removeEventListener('mouseleave', this.handleTouchEnd);
        this.track.removeEventListener('transitionend', this.handleTransitionEnd);
      }

      this.teardownPointerDrag();
      this.teardownAutoScroll();

      this.removeEventListener('keydown', this.handleKeyDown);
      window.removeEventListener('resize', this.handleResize);

      if (this.animationID) {
        cancelAnimationFrame(this.animationID);
      }
    }

    /* ----------------------------------------------------------------------
       Pointer drag (added 2026-07-27)

       This carousel is TRANSFORM based: .hanlyn-shop-pattern__track-container
       is overflow-x: hidden and never scrolled, while
       .hanlyn-shop-pattern__track is moved with translateX(). The drag below
       therefore works against the same transform, and hands the release back to
       the existing centring code (updateCarousel / navigate) rather than
       inventing a parallel positioning mechanism.
       ---------------------------------------------------------------------- */

    setupPointerDrag() {
      // Never bind twice, e.g. if the element is disconnected and reconnected
      // by the theme editor.
      if (this.pointerDragBound) return;
      if (typeof window.PointerEvent === 'undefined') return;

      // Homepage instance only. The collection page renders this same markup
      // inside .hanlyn-shop-pattern--embedded and is deliberately left alone.
      if (typeof this.closest === 'function' && this.closest('.hanlyn-shop-pattern--embedded')) return;

      const container = this.querySelector('.hanlyn-shop-pattern__track-container');
      if (!container || !this.track) return;

      this.dragContainer = container;
      // Only pointerdown is bound to the container. move/up/cancel go on window
      // for the lifetime of a gesture (see addPointerGestureListeners), so a
      // release outside the carousel still ends the drag and still clears every
      // one-shot listener - no pointer capture is needed to hear about it.
      container.addEventListener('pointerdown', this.handlePointerDown);
      container.addEventListener('dragstart', this.handleDragStart);
      this.pointerDragBound = true;
    }

    // Window-level listeners so the gesture is followed wherever the pointer
    // goes. Guarded so a second pointerdown cannot register them twice.
    addPointerGestureListeners() {
      if (this.pointerGestureBound) return;
      window.addEventListener('pointermove', this.handlePointerMove);
      window.addEventListener('pointerup', this.handlePointerUp);
      window.addEventListener('pointercancel', this.handlePointerUp);
      this.pointerGestureBound = true;
    }

    removePointerGestureListeners() {
      if (!this.pointerGestureBound) return;
      window.removeEventListener('pointermove', this.handlePointerMove);
      window.removeEventListener('pointerup', this.handlePointerUp);
      window.removeEventListener('pointercancel', this.handlePointerUp);
      this.pointerGestureBound = false;
    }

    teardownPointerDrag() {
      this.removePointerGestureListeners();
      this.clearClickSuppressor();

      if (!this.pointerDragBound || !this.dragContainer) return;

      this.dragContainer.removeEventListener('pointerdown', this.handlePointerDown);
      this.dragContainer.removeEventListener('dragstart', this.handleDragStart);
      this.dragContainer.classList.remove('is-dragging');

      this.pointerDragBound = false;
      this.pointerDragActive = false;
      this.pointerDragCommitted = false;
      this.activePointerId = null;
      this.dragContainer = null;
    }

    // Native link/image dragging would hijack the gesture in engines that do
    // not honour -webkit-user-drag. Only blocked while we are watching a press.
    handleDragStart(e) {
      if (this.pointerDragActive) e.preventDefault();
    }

    handlePointerDown(e) {
      // Let the browser own touch scrolling. The touchstart/touchmove handlers
      // above already provide the mobile swipe, so touch is skipped here to
      // avoid two mechanisms fighting over the same gesture.
      if (e.pointerType === 'touch') return;
      if (e.isPrimary === false || e.button > 0) return;
      if (!this.track || !this.dragContainer) return;

      // Never start a drag from a control.
      if (e.target && e.target.closest &&
          e.target.closest('.hanlyn-shop-pattern__arrow, .hanlyn-shop-pattern__button')) return;

      // Do not interrupt an arrow/keyboard transition already in flight -
      // killing its transition would strand isTransitioning at true.
      if (this.isTransitioning || this.pointerDragActive) return;

      // Pause the drift BEFORE the start position is captured below. Stopping
      // can trigger a clone-band rebase, which shifts currentTranslate by
      // exactly one band period; capturing the pre-rebase value would leave the
      // drag working from a stale baseline and slide a whole band on release.
      this.autoScrollDragging = true;
      this.updateAutoScrollState();

      // ARMED, not committed. Nothing here may interfere with the click that
      // may be about to happen: no preventDefault (which would suppress the
      // compatibility mousedown/mouseup and the anchor's focus), and above all
      // NO setPointerCapture - capturing retargets the whole pointer stream to
      // the container, which moves the synthesised click's target up to the
      // container too and takes the <a> off the event path entirely. Everything
      // that can disturb a click waits for commitPointerDrag().
      this.pointerDragActive = true;
      this.pointerDragCommitted = false;
      this.activePointerId = e.pointerId;
      this.pointerStartX = e.clientX;
      this.pointerStartTranslate = this.currentTranslate;
      this.pointerMovedBy = 0;

      // The legacy mousedown handler bails on pointerDragActive, so the old
      // mouse-drag path stays out of the way without needing preventDefault.
      this.addPointerGestureListeners();
    }

    handlePointerMove(e) {
      if (!this.pointerDragActive || e.pointerId !== this.activePointerId) return;
      if (!this.track) return;

      this.pointerMovedBy = e.clientX - this.pointerStartX;

      if (!this.pointerDragCommitted) {
        // Still inside the dead zone - this could yet be a click, so the track
        // is not moved and nothing is armed.
        if (Math.abs(this.pointerMovedBy) < DRAG_THRESHOLD) return;
        this.commitPointerDrag(e);
      }

      // Committed: suppress the compatibility mousemove so the browser does not
      // extend a text selection under the cursor while dragging.
      if (e.cancelable) e.preventDefault();

      this.currentTranslate = this.pointerStartTranslate + this.pointerMovedBy;
      this.setTrackPosition();
    }

    // The moment a press becomes a drag. Everything that interferes with a
    // click lives here, and here only.
    commitPointerDrag(e) {
      this.pointerDragCommitted = true;

      // Safe now: the click ending this gesture is one we intend to suppress,
      // so retargeting it to the container is harmless either way.
      try {
        this.dragContainer.setPointerCapture(e.pointerId);
      } catch (err) {
        /* capture is a robustness nicety; the window listeners already suffice */
      }

      this.dragContainer.classList.add('is-dragging');
      this.track.style.transition = 'none';

      // Drop any selection the press started before it became a drag.
      const selection = typeof window.getSelection === 'function' ? window.getSelection() : null;
      if (selection && !selection.isCollapsed && typeof selection.removeAllRanges === 'function') {
        selection.removeAllRanges();
      }
    }

    handlePointerUp(e) {
      if (!this.pointerDragActive || e.pointerId !== this.activePointerId) return;

      const committed = this.pointerDragCommitted;
      const cancelled = e.type === 'pointercancel';
      const delta = this.pointerMovedBy;

      // Clear all gesture state first, on every path - including a release that
      // happens outside the carousel, which reaches us via the window listeners.
      this.pointerDragActive = false;
      this.pointerDragCommitted = false;
      this.activePointerId = null;
      this.pointerMovedBy = 0;
      this.removePointerGestureListeners();

      if (this.dragContainer) {
        try {
          if (this.dragContainer.hasPointerCapture &&
              this.dragContainer.hasPointerCapture(e.pointerId)) {
            this.dragContainer.releasePointerCapture(e.pointerId);
          }
        } catch (err) {
          /* no-op */
        }
        this.dragContainer.classList.remove('is-dragging');
      }

      // Never committed: the pointer moved less than DRAG_THRESHOLD, so this
      // was a click. commitPointerDrag() never ran, which means nothing was
      // captured, nothing was cancelled and the track's transition was never
      // touched - the DOM is exactly as the browser left it and the anchor
      // activates normally. Arming a suppressor here is what killed the tiles.
      if (!committed) {
        this.autoScrollDragging = false;
        this.scheduleAutoScrollResume();
        return;
      }

      if (!this.track) return;

      // Restore the CSS transition so the snap animates.
      this.track.style.transition = '';

      // Suppress exactly one click: the one that ends this drag. A
      // pointercancel is never followed by a click, so nothing is armed there.
      if (!cancelled) {
        this.installClickSuppressor();
      }

      // A completed drag is explicit user control - the drift is done.
      this.stopAutoScrollPermanently();

      // Snap to the nearest tile. Tiles are uniform, so one step is one tile
      // plus one gap.
      const step = this.getItemWidth() + this.getItemGap();
      const indexDelta = step > 0 ? Math.round(-delta / step) : 0;
      const target = this.currentIndex + indexDelta;

      if (indexDelta !== 0 && target >= this.totalItems && target < this.totalItems * 2) {
        // Still inside the band of real items - reuse the existing centring.
        this.currentIndex = target;
        this.updateCarousel(true);
      } else if (indexDelta !== 0) {
        // The target lands in a cloned band. Hand off to navigate() so the
        // clone wrap-around bookkeeping stays in one place.
        this.navigate(indexDelta > 0 ? 1 : -1);
      } else {
        // Less than one tile of travel - snap back to the current tile.
        this.updateCarousel(true);
      }
    }

    // One-shot, capture-phase click blocker used after a drag.
    installClickSuppressor() {
      if (!this.dragContainer) return;
      this.clearClickSuppressor();

      const container = this.dragContainer;
      const suppress = (evt) => {
        evt.preventDefault();
        evt.stopPropagation();
        this.clearClickSuppressor();
      };

      this.suppressClickHandler = suppress;
      container.addEventListener('click', suppress, true);

      // Safety net: if no click follows the drag, drop the listener anyway so a
      // later, genuine click is never swallowed.
      this.suppressClickTimer = window.setTimeout(() => this.clearClickSuppressor(), 100);
    }

    clearClickSuppressor() {
      if (this.suppressClickTimer) {
        window.clearTimeout(this.suppressClickTimer);
        this.suppressClickTimer = null;
      }
      if (this.suppressClickHandler && this.dragContainer) {
        this.dragContainer.removeEventListener('click', this.suppressClickHandler, true);
      }
      this.suppressClickHandler = null;
    }

    /* ----------------------------------------------------------------------
       Auto-scroll (added 2026-07-28)

       A slow continuous drift rather than a stepped slide. The track is moved
       by mutating this.currentTranslate every animation frame by
       AUTO_SCROLL_SPEED * deltaTime, so the speed is frame-rate independent.

       Keeping currentIndex honest: the resting offset for an index is
       `containerCenter - (index * step + itemWidth / 2)`, so travelling one
       `step` (tile width + gap) is exactly one index. The loop therefore
       accumulates drifted distance and bumps currentIndex once per step, which
       means an arrow click or a drag after the drift continues from the tile
       the visitor is actually looking at instead of snapping back.

       Clone-band wrap-around is NOT reimplemented here. When currentIndex
       leaves the real band the loop calls the component's own
       handleTransitionEnd(), which is the existing rebase: it moves the index
       by exactly one band and re-centres via updateCarousel(false). Because the
       three bands are identical clones, that shift is pixel-for-pixel invisible.
       handleTransitionEnd only ever fired on `transitionend` before, and the
       drift deliberately runs with transitions off, so this call is the
       equivalent trigger for the rAF path - same code, different entry point.
       ---------------------------------------------------------------------- */

    setupAutoScroll() {
      if (this.autoScrollEnabled) return; // never wire up twice
      if (!this.track || this.totalItems === 0) return;

      // Homepage instance only, matching the pointer-drag scoping.
      if (typeof this.closest === 'function' && this.closest('.hanlyn-shop-pattern--embedded')) return;

      // Reduced motion: never start at all. Kept as a live MediaQueryList so
      // updateAutoScrollState() re-reads it rather than caching the answer.
      if (typeof window.matchMedia === 'function') {
        this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        if (this.reducedMotionQuery.matches) return;
      }

      this.autoScrollEnabled = true;

      this.addEventListener('pointerenter', this.handleAutoScrollPointerEnter);
      this.addEventListener('pointerleave', this.handleAutoScrollPointerLeave);
      this.addEventListener('focusin', this.handleAutoScrollFocusIn);
      this.addEventListener('focusout', this.handleAutoScrollFocusOut);

      // Capture phase, so these run BEFORE the component's own click/keydown
      // handlers further down the tree. Stopping the drift restores the CSS
      // transition, so the arrow move that follows still eases instead of
      // snapping instantly.
      this.addEventListener('click', this.handleUserTakeover, true);
      this.addEventListener('keydown', this.handleUserTakeover, true);
      this.addEventListener('touchstart', this.handleAutoScrollTouchStart, { capture: true, passive: true });
      this.addEventListener('touchend', this.handleAutoScrollTouchEnd, { capture: true, passive: true });

      document.addEventListener('visibilitychange', this.handleAutoScrollVisibility);

      // Do not burn frames on a carousel that is scrolled out of view.
      if (typeof window.IntersectionObserver === 'function') {
        this.autoScrollVisible = false; // the observer's first callback decides
        this.autoScrollObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            this.autoScrollVisible = entry.isIntersecting;
          });
          this.updateAutoScrollState();
        }, { threshold: 0 });
        this.autoScrollObserver.observe(this);
      }

      this.updateAutoScrollState();
    }

    teardownAutoScroll() {
      this.stopAutoScrollLoop();
      this.clearAutoScrollResumeTimer();

      if (this.autoScrollObserver) {
        this.autoScrollObserver.disconnect();
        this.autoScrollObserver = null;
      }

      if (this.autoScrollEnabled) {
        this.removeEventListener('pointerenter', this.handleAutoScrollPointerEnter);
        this.removeEventListener('pointerleave', this.handleAutoScrollPointerLeave);
        this.removeEventListener('focusin', this.handleAutoScrollFocusIn);
        this.removeEventListener('focusout', this.handleAutoScrollFocusOut);
        this.removeEventListener('click', this.handleUserTakeover, true);
        this.removeEventListener('keydown', this.handleUserTakeover, true);
        this.removeEventListener('touchstart', this.handleAutoScrollTouchStart, true);
        this.removeEventListener('touchend', this.handleAutoScrollTouchEnd, true);
        document.removeEventListener('visibilitychange', this.handleAutoScrollVisibility);
      }

      // autoScrollStopped is intentionally NOT reset: "stopped" is meant to
      // survive for the page view, including a theme-editor re-render.
      this.autoScrollEnabled = false;
      this.autoScrollHovered = false;
      this.autoScrollFocused = false;
      this.autoScrollDragging = false;
    }

    // Single place that decides whether the drift should be running.
    updateAutoScrollState() {
      if (!this.autoScrollEnabled) return;

      const reducedMotion = this.reducedMotionQuery ? this.reducedMotionQuery.matches : false;
      const canRun =
        !this.autoScrollStopped &&
        !reducedMotion &&
        !this.autoScrollHovered &&
        !this.autoScrollFocused &&
        !this.autoScrollDragging &&
        !this.isDragging &&
        !this.pointerDragActive &&
        this.autoScrollVisible &&
        document.visibilityState !== 'hidden';

      if (canRun) {
        this.startAutoScrollLoop();
      } else {
        this.stopAutoScrollLoop();
      }
    }

    startAutoScrollLoop() {
      if (this.autoScrollRafId !== null) return; // guard against double-starting
      if (!this.track) return;

      this.autoScrollStep = this.getItemWidth() + this.getItemGap();
      // autoScrollAccum is deliberately NOT reset here. It measures how far
      // past the resting offset for currentIndex the track has drifted, and it
      // has to survive a hover pause - zeroing it on every resume would let
      // currentIndex fall further behind the visible position each time.
      this.autoScrollLast = 0;

      // The 0.4s ease on .hanlyn-shop-pattern__track would fight a per-frame
      // transform, so it is switched off for the duration of the drift and
      // handed back in stopAutoScrollLoop().
      this.track.style.transition = 'none';
      this.autoScrollOwnsTransition = true;

      this.autoScrollRafId = requestAnimationFrame(this.autoScrollTick);
    }

    stopAutoScrollLoop() {
      if (this.autoScrollRafId !== null) {
        cancelAnimationFrame(this.autoScrollRafId);
        this.autoScrollRafId = null;
      }

      // Round currentIndex to the tile actually nearest the centre, so an arrow
      // click or a drag straight after a pause moves by a sensible amount
      // instead of a fraction. The matching adjustment to autoScrollAccum keeps
      // the invariant `currentTranslate === restingOffset(currentIndex) -
      // autoScrollAccum` true, so nothing moves on screen. Idempotent: after
      // this, accum is always <= step / 2.
      // Skipped while a pointer or touch gesture is in progress. The rebase it
      // can trigger shifts the strip by one clone-band period - visually
      // identical, but it swaps the DOM node under the cursor for its clone,
      // which would leave the press and the release on different elements and
      // move the synthesised click's target off the anchor. Pure bookkeeping,
      // so the next pause picks it up instead.
      if (!this.autoScrollDragging &&
          this.autoScrollStep > 0 && this.autoScrollAccum > this.autoScrollStep / 2) {
        this.currentIndex += 1;
        this.autoScrollAccum -= this.autoScrollStep;
        // Rounding up can push the index one past the real band; rebase now
        // rather than leaving it out of range for the next arrow click.
        this.rebaseAutoScrollIfNeeded();
      }

      if (this.autoScrollOwnsTransition) {
        this.autoScrollOwnsTransition = false;
        // Only give the transition back if a drag has not since claimed it -
        // both drag paths set and clear `transition: none` themselves.
        if (this.track && !this.isDragging && !this.pointerDragActive) {
          this.track.style.transition = '';
        }
      }
    }

    // Explicit navigation is final: the drift does not come back this page view.
    stopAutoScrollPermanently() {
      this.autoScrollStopped = true;
      this.clearAutoScrollResumeTimer();
      this.stopAutoScrollLoop();
    }

    /* The single wrap-around entry point for the drift. It does NOT implement a
       second wrap: handleTransitionEnd() is the component's existing rebase and
       does all the work (shift the index by one clone band, re-centre via
       updateCarousel(false)). The only addition is restoring the sub-tile
       remainder that updateCarousel(false) discards when it pins the track to
       the exact resting offset. With the remainder restored, the rebase shifts
       the strip by exactly one band period - and because the three bands are
       identical clones, a shift of exactly one period renders pixel for pixel
       the same frame. Drop the remainder and the strip nudges on every wrap. */
    rebaseAutoScrollIfNeeded() {
      if (this.currentIndex >= this.totalItems * 2 || this.currentIndex < this.totalItems) {
        this.handleTransitionEnd();
        this.currentTranslate -= this.autoScrollAccum;
        this.prevTranslate = this.currentTranslate;
        this.setTrackPosition();
      }
    }

    autoScrollTick(now) {
      if (this.autoScrollRafId === null) return;
      if (!this.track) {
        this.stopAutoScrollLoop();
        return;
      }

      if (!this.autoScrollLast) this.autoScrollLast = now;
      const dt = Math.min((now - this.autoScrollLast) / 1000, AUTO_SCROLL_MAX_FRAME);
      this.autoScrollLast = now;

      const distance = AUTO_SCROLL_SPEED * dt;
      this.currentTranslate -= distance;
      this.autoScrollAccum += distance;

      const step = this.autoScrollStep;
      if (step > 0 && this.autoScrollAccum >= step) {
        const advanced = Math.floor(this.autoScrollAccum / step);
        this.autoScrollAccum -= advanced * step;
        this.currentIndex += advanced;

        this.rebaseAutoScrollIfNeeded();

        // Re-measure once per tile so a resize across a breakpoint (216px ->
        // 168px tiles) is picked up without measuring every frame.
        this.autoScrollStep = this.getItemWidth() + this.getItemGap();
      }

      // Keep the drag baseline on the visible position, so a swipe that starts
      // mid-drift does not jump.
      this.prevTranslate = this.currentTranslate;
      this.setTrackPosition();

      this.autoScrollRafId = requestAnimationFrame(this.autoScrollTick);
    }

    clearAutoScrollResumeTimer() {
      if (this.autoScrollResumeTimer) {
        window.clearTimeout(this.autoScrollResumeTimer);
        this.autoScrollResumeTimer = null;
      }
    }

    // Used after a gesture that did NOT count as navigation. Deferred so the
    // snap-back animation is never cut short by re-applying `transition: none`.
    scheduleAutoScrollResume() {
      if (!this.autoScrollEnabled || this.autoScrollStopped) return;
      this.clearAutoScrollResumeTimer();
      this.autoScrollResumeTimer = window.setTimeout(() => {
        this.autoScrollResumeTimer = null;
        this.updateAutoScrollState();
      }, AUTO_SCROLL_RESUME_DELAY);
    }

    handleAutoScrollPointerEnter() {
      this.autoScrollHovered = true;
      this.updateAutoScrollState();
    }

    handleAutoScrollPointerLeave() {
      this.autoScrollHovered = false;
      this.updateAutoScrollState();
    }

    handleAutoScrollFocusIn() {
      this.autoScrollFocused = true;
      this.updateAutoScrollState();
    }

    handleAutoScrollFocusOut() {
      this.autoScrollFocused = false;
      this.updateAutoScrollState();
    }

    handleAutoScrollVisibility() {
      this.updateAutoScrollState();
    }

    handleAutoScrollTouchStart() {
      this.autoScrollDragging = true;
      this.updateAutoScrollState();
    }

    handleAutoScrollTouchEnd() {
      // Capture phase runs before the track's own touchend handler, so
      // hasMoved still reflects the swipe that just finished.
      if (this.hasMoved) {
        this.stopAutoScrollPermanently();
        return;
      }
      this.autoScrollDragging = false;
      this.scheduleAutoScrollResume();
    }

    handleUserTakeover(e) {
      if (!this.autoScrollEnabled || this.autoScrollStopped) return;

      if (e.type === 'keydown') {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      } else {
        // Arrows, plus a dot control if this component ever grows one. The
        // Shop Now button is not navigation of the carousel, so it is ignored.
        const control = e.target && e.target.closest
          ? e.target.closest('.hanlyn-shop-pattern__arrow, .hanlyn-shop-pattern__dot')
          : null;
        if (!control) return;
      }

      this.stopAutoScrollPermanently();
    }

    handlePrevClick() {
      this.navigate(-1);
    }

    handleNextClick() {
      this.navigate(1);
    }

    handleKeyDown(e) {
      if (this.isTransitioning) return;

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.navigate(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.navigate(1);
      }
    }

    handleTouchStart(e) {
      // Belt and braces: the pointer-drag path owns mouse/pen gestures. Cancelling
      // pointerdown already suppresses the compatibility mousedown, but bail here
      // too so a browser that still emits it cannot start a second drag.
      if (this.pointerDragActive) return;

      // Prevent dragging on arrow buttons and shop button, but allow on pattern cards
      if (e.target.closest('.hanlyn-shop-pattern__arrow, .hanlyn-shop-pattern__button')) return;

      this.isDragging = true;
      this.startX = this.getPositionX(e);
      this.startY = this.getPositionY(e);
      this.hasMoved = false;
      this.swipeDirection = null; // Track if horizontal or vertical
      this.animationID = requestAnimationFrame(() => this.animation());

      // Disable transitions during drag for smoother experience
      this.track.style.transition = 'none';
    }

    handleTouchMove(e) {
      if (!this.isDragging) return;

      const currentPosition = this.getPositionX(e);
      const currentY = this.getPositionY(e);
      const deltaX = Math.abs(currentPosition - this.startX);
      const deltaY = Math.abs(currentY - this.startY);

      // Determine swipe direction once we have enough movement
      if (this.swipeDirection === null && (deltaX > 5 || deltaY > 5)) {
        // If horizontal movement is greater, it's a horizontal swipe
        this.swipeDirection = deltaX > deltaY ? 'horizontal' : 'vertical';
      }

      // Only prevent default and handle carousel drag if it's a horizontal swipe
      if (this.swipeDirection === 'horizontal') {
        e.preventDefault();
        this.currentTranslate = this.prevTranslate + currentPosition - this.startX;

        // Track if user has moved significantly
        if (deltaX > 3) {
          this.hasMoved = true;
        }
      } else if (this.swipeDirection === 'vertical') {
        // It's a vertical scroll, stop tracking
        this.isDragging = false;
        cancelAnimationFrame(this.animationID);
        this.track.style.transition = '';
        this.currentTranslate = this.prevTranslate;
        this.setTrackPosition();
      }
    }

    getPositionY(e) {
      return e.type.includes('mouse') ? e.pageY : e.touches[0].clientY;
    }

    handleTouchEnd() {
      if (!this.isDragging) return;

      this.isDragging = false;
      cancelAnimationFrame(this.animationID);

      // Re-enable transitions
      this.track.style.transition = '';

      // Determine if swipe was significant enough to change slides
      const movedBy = this.currentTranslate - this.prevTranslate;
      // Reduced threshold to make it more responsive (was / 3, now / 5)
      const threshold = this.getItemWidth() / 5;

      if (movedBy < -threshold && !this.isTransitioning) {
        this.navigate(1);
      } else if (movedBy > threshold && !this.isTransitioning) {
        this.navigate(-1);
      } else {
        // Snap back to current position
        this.updateCarousel(true);
      }

      // Reset flags after a short delay (after click event fires)
      setTimeout(() => {
        this.hasMoved = false;
        this.swipeDirection = null;
      }, 10);
    }

    handleResize() {
      this.updateCarousel(false);
    }

    getPositionX(e) {
      return e.type.includes('mouse') ? e.pageX : e.touches[0].clientX;
    }

    getItemWidth() {
      const allItems = this.track.querySelectorAll('.hanlyn-shop-pattern__item');
      return allItems[0] ? allItems[0].offsetWidth : 120;
    }

    getItemGap() {
      const styles = window.getComputedStyle(this.track);
      return parseFloat(styles.gap) || 6;
    }

    animation() {
      if (this.isDragging) {
        this.setTrackPosition();
        this.animationID = requestAnimationFrame(() => this.animation());
      }
    }

    setTrackPosition() {
      this.track.style.transform = `translateX(${this.currentTranslate}px)`;
    }

    navigate(direction) {
      if (this.isTransitioning) return;

      this.currentIndex += direction;
      this.isTransitioning = true;
      this.updateCarousel(true);
    }

    handleTransitionEnd() {
      this.isTransitioning = false;

      // If we're at a clone, jump to the real item without transition
      if (this.currentIndex >= this.totalItems * 2) {
        // We're past the last real item, jump to first real item
        this.currentIndex = this.totalItems;
        this.updateCarousel(false);
      } else if (this.currentIndex < this.totalItems) {
        // We're before the first real item, jump to last real item
        this.currentIndex = this.totalItems * 2 - 1;
        this.updateCarousel(false);
      }
    }

    updateCarousel(animate = true) {
      // Get all items including clones. Tiles are a uniform size (set in CSS),
      // so we only need to center the current item — no per-item scaling.
      const allItems = Array.from(this.track.querySelectorAll('.hanlyn-shop-pattern__item'));
      const isMobile = window.innerWidth <= 749;
      const baseWidth = isMobile ? 120 : 216; // Fallback if offsetWidth is unavailable

      // Force reflow to ensure width measurements are accurate
      this.track.offsetHeight;

      // Calculate position to center the current item
      const trackContainer = this.track.parentElement;
      const containerCenter = trackContainer.offsetWidth / 2;
      const gap = this.getItemGap();

      // Sum widths of all items before the current index
      let itemPosition = 0;
      for (let i = 0; i < this.currentIndex; i++) {
        const itemWidth = allItems[i].offsetWidth || baseWidth;
        itemPosition += itemWidth + gap;
      }

      // Add half of current item's width to reach its center
      const currentItem = allItems[this.currentIndex];
      const currentItemWidth = currentItem.offsetWidth || baseWidth;
      itemPosition += currentItemWidth / 2;

      // Offset needed to center the current item
      const offset = containerCenter - itemPosition;

      this.currentTranslate = offset;
      this.prevTranslate = offset;

      // Apply transform
      if (animate) {
        this.track.style.transform = `translateX(${offset}px)`;
      } else {
        // Skip transition for instant jumps
        const transition = this.track.style.transition;
        this.track.style.transition = 'none';
        this.track.style.transform = `translateX(${offset}px)`;
        // Force reflow
        this.track.offsetHeight;
        this.track.style.transition = transition;
      }
    }

    // Utility: Debounce helper
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }
  }

  customElements.define('shop-by-pattern-carousel', ShopByPatternCarousel);
}
